import { useState, type ReactNode } from 'react';
import { cn } from './cn';
import { fileUrl } from '../api/client';
import { IDENTITY_SERIES } from '../lib/palette';

/**
 * A NAMED THING'S IDENTITY MARK — initials (or an icon) on a hue derived from the name itself.
 *
 * Every register in the app opens on the same picture: a column of black names down the left edge and
 * grey text everywhere else, so one row looks exactly like the next and the eye has nothing to anchor
 * on when scrolling back to "the one I was just looking at". A per-row mark fixes that, but only if
 * its colour MEANS something stable — a random hue per render, or one hue for the whole list, is just
 * decoration.
 *
 * So the hue is a pure function of the name: the same transporter, warehouse or person is the same
 * colour on every page that draws them, and adjacent rows are almost always different colours. The
 * palette is the brand's own eight chart tokens rather than a rainbow, so a list of twenty reads as
 * one family instead of a bag of highlighters.
 *
 * Status does NOT live here. A retired or blocked row says so in its own tag; tinting the mark by
 * state would collide with the identity meaning and leave neither readable.
 */

/**
 * The identity ramp (lib/palette) — real colour values, deliberately not `var(--color-chart-N)`:
 * Tailwind v4 only emits the theme vars a CLASS references, so tokens read from an inline style
 * resolved to nothing and every mark came out colourless (transparent tint, inherited text).
 */
const RAMP = IDENTITY_SERIES;
const SLOTS = RAMP.length;

/** Stable, order-independent hash → a slot in [1, SLOTS]. FNV-1a: cheap, and well spread for the
 *  short strings a name column holds (two names differing in one letter land far apart). */
function slotOf(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (Math.abs(h) % SLOTS) + 1;
}

/**
 * The stable hue a named thing wears app-wide — the same warehouse, transporter or person is the same
 * colour on every page that draws it. Exported so a chip that is NOT an avatar (a warehouse code, say)
 * can join the same identity system instead of inventing a second palette.
 */
export function entityHue(name?: string | null): string {
  return RAMP[slotOf(String(name ?? '').trim().toLowerCase() || '?') - 1];
}

/**
 * Up to two initials from a name: "Maharashtra Roadways" → MR, "Delhivery (demo)" → D.
 * Non-letters are skipped so a name like "trtt" or "27AABCM…" still yields something readable.
 */
export function initialsOf(name?: string | null): string {
  const words = String(name ?? '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  const first = words[0][0] ?? '';
  const second = words.length > 1 ? (words[words.length - 1][0] ?? '') : '';
  return (first + second).toUpperCase();
}

export interface EntityAvatarProps {
  /** The name the colour and the initials are derived from. */
  name?: string | null;
  /** Photo / logo, when the record has one — it wins over initials. */
  src?: string | null;
  /** Shown instead of initials (a Truck for a carrier, a Warehouse for a location). */
  icon?: ReactNode;
  /** Diameter in px. */
  size?: number;
  /** Square with soft corners instead of a circle — right for a THING (warehouse, product, carrier);
   *  the circle stays for a PERSON. */
  square?: boolean;
  /** Drains the colour, for a row that is retired / inactive. */
  muted?: boolean;
  /**
   * A DIFFERENT stable identity map for the same thing — currently only the per-product packaging
   * accent (`lib/productAccent`), which is the colour that product already wears on the public site,
   * in the stock table and in the valuation chart. Passing it here keeps one product one colour
   * everywhere instead of giving it a name-hash hue in registers and its packaging hue in charts.
   *
   * It must stay a pure function of the thing's IDENTITY. Never pass a status colour: a mark that
   * turns red when a lot expires collides with the identity meaning and leaves neither readable.
   */
  hue?: string;
  className?: string;
}

export function EntityAvatar({ name, src, icon, size = 34, square, muted, hue: hueOverride, className }: EntityAvatarProps) {
  // A photo that 404s (a record pointing at a deleted upload) used to leave an EMPTY tinted box —
  // worse than no photo, because the identity mark is exactly what the row is anchored by. A broken
  // image falls back to the initials, which never fail.
  const [broken, setBroken] = useState(false);
  const hue = hueOverride ?? entityHue(name);
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-grid shrink-0 place-items-center overflow-hidden type-caption-strong leading-none',
        square ? 'rounded-control' : 'rounded-pill',
        muted && 'opacity-45 grayscale',
        className,
      )}
      style={{
        width: size,
        height: size,
        // color-mix keeps ONE source of truth for the hue: the tint, the ring and the glyph are all
        // the same token, so a palette change moves them together and dark mode needs no second set.
        background: `color-mix(in srgb, ${hue} 20%, transparent)`,
        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 42%, transparent)`,
        color: hue,
        fontSize: Math.max(10, Math.round(size * 0.36)),
      }}
    >
      {src && !broken ? (
        <>
          <img src={fileUrl(src)} alt="" onError={() => setBroken(true)} className="h-full w-full object-cover" />
          {/*
           * The ring, redrawn ON TOP of the photo.
           *
           * An inset box-shadow paints under a child element, so the mark's hairline vanishes the
           * moment there is a picture in it. That is invisible on a dark pack shot and total on a
           * light one: a bottle photographed on a white studio background, dropped on a white table
           * row, became a 32px blank — the row looked like it had lost its identity mark rather than
           * gained a photograph. Same hue, same 1px, just at a layer the image cannot cover.
           */}
          <span
            aria-hidden
            className={cn('pointer-events-none absolute inset-0', square ? 'rounded-control' : 'rounded-pill')}
            style={{ boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 45%, transparent)` }}
          />
        </>
      ) : (icon ?? initialsOf(name))}
    </span>
  );
}
